import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { Observable, of, OperatorFunction, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { Case, CaseType } from 'src/app/models/case/case.interface';
import {
  PaginatedData,
  PaginatedDataResponse,
} from 'src/app/models/common/common.interface';
import { CaseService } from 'src/app/services/case.service';

@Component({
  selector: 'app-case',
  templateUrl: './case.component.html',
  styleUrls: ['./case.component.css'],
})
export class CaseComponent implements OnInit {
  public caseColumns = [
    { key: 'case_number', name: 'Case Number', selected: false },
    { key: 'case_status__name', name: 'Case Status', selected: false },
    { key: 'claim__client__name', name: 'Client', selected: false },
    { key: 'case_type__name', name: 'Case Type', selected: false },
    { key: 'flags', name: 'Flags', selected: false },
    { key: 'due_date', name: 'Due Date', selected: false },
    { key: 'created_at', name: 'Date Created', selected: false },
    { key: 'modified_at', name: 'Last Updated', selected: false },
  ];
  casePage = 1;
  caseLimit = 20;
  Case: Case;
  @BlockUI('case-list-ui') caseListBlockUI: NgBlockUI;
  cases: Case[];
  caseTotal: number;
  caseListOrderBy = 'modified_at';
  caseListOrderDirection = 'desc';
  searchCaseText = new FormControl('');
  searchByValue = new FormControl('');
  allCaseTypeLoader: boolean;
  caseTypes: CaseType[];
  private destroy$ = new Subject<void>();
  selectedCaseType = '';
  filterRequest = {};
  public clientDateFormat = 'MM/dd/yyyy';

  constructor(private caseService: CaseService) {}

  ngOnInit() {
    this.getCaseList();
    // this.setSearchTextChange();
  }

  getCaseList() {
    this.caseListBlockUI.start();
    this.filterRequest['limit'] = this.caseLimit;
    this.filterRequest['offset'] = this.casePage;
    this.filterRequest['order_by'] = this.caseListOrderBy;
    this.filterRequest['order_direction'] = this.caseListOrderDirection;

    this.caseService
      .getCases(this.filterRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp: PaginatedDataResponse<PaginatedData<Case>>) => {
          const { hits, offset, estimatedTotalHits } = resp.details;
          this.cases = hits;
          this.caseTotal = estimatedTotalHits;
          this.caseListBlockUI.stop();
        },
        (error) => {
          this.caseListBlockUI.stop();
        }
      );
  }

  setSearchTextChange() {
    this.searchCaseText.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500),
        distinctUntilChanged(),
        filter((term) => term && term.length >= 3),
        switchMap((term) => {
          this.caseListBlockUI.start();
          this.filterRequest['search'] = term;
          return this.caseService.getCases(this.filterRequest);
        })
      )
      .subscribe(
        (resp: PaginatedDataResponse<PaginatedData<Case>>) => {
          const { hits, offset, estimatedTotalHits } = resp.details;
          this.cases = hits;
          this.caseTotal = estimatedTotalHits;
          this.caseListBlockUI.stop();
        },
        (error) => {
          this.caseListBlockUI.stop();
        }
      );
  }

  sortColumn(column: string) {
    this.caseListOrderBy = column;
    this.caseListOrderDirection =
      this.caseListOrderDirection === 'asc' ? 'desc' : 'asc';
    this.getCaseList();
  }

  onPageChangeCases(event: any) {
    this.casePage = event;
    this.getCaseList();
  }

  onSizeChangeCases(limit: number) {
    this.caseLimit = limit;
    this.casePage = 1;
    this.getCaseList();
  }

  caseTypeFormatter = (x: any) => x.name;

  searchCaseType: OperatorFunction<string, readonly any[]> = (
    text$: Observable<string>
  ) =>
    text$.pipe(
      tap((term) => {
        if (term.length <= 2) {
          this.allCaseTypeLoader = false;
        }
      }),
      debounceTime(1000),
      distinctUntilChanged(),
      tap((term) => {
        if (term.length > 2) {
          this.allCaseTypeLoader = true;
        }
      }),
      switchMap((term) =>
        term.length < 3
          ? []
          : this.caseService.getCaseType(20, 1, term).pipe(
              tap((result) => {
                if (result) {
                  this.allCaseTypeLoader = false;
                }
              }),
              map((results) => {
                this.caseTypes = results;
                const startsWithResults = this.caseTypes.filter(
                  (result: any): any =>
                    result.name.toLowerCase().startsWith(term.toLowerCase())
                );
                const otherResults = this.caseTypes.filter(
                  (result: any) =>
                    !result.name.toLowerCase().startsWith(term.toLowerCase())
                );
                return [...startsWithResults, ...otherResults];
              }),
              catchError(() => {
                this.allCaseTypeLoader = false;
                return of([]);
              })
            )
      )
    );

  selectAllCaseType(caseType: any) {
    this.selectedCaseType = caseType.name;
    this.filterRequest['case_type__name'] = this.selectedCaseType;
    this.allCaseTypeLoader = false;
    this.getCaseList();
  }

  onEnterPressed() {

    if(this.searchCaseText.value && this.searchCaseText.value.trim().length <= 2) {
      return;
    }

    const keysToRemove = ["subject_full_name", "client__name", "case_number", "search"];
    keysToRemove.forEach(key => delete this.filterRequest[key]);

    switch (this.searchByValue.value) {
      case 'subject':
        this.filterRequest['subject_full_name'] = this.searchCaseText.value;
        break;
      case 'client':
        this.filterRequest['client__name'] = this.searchCaseText.value;
        break;
      case 'case_number':
        this.filterRequest['case_number'] = this.searchCaseText.value;
        break;
      default:
        this.filterRequest['search'] = this.searchCaseText.value;
        break;
    }

    this.getCaseList();
  }

  getNames(items: any[]): string {
    return items?.length ? items.map(item => item.name).join(', ') : '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
